// Basic
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
// Libs
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
// Components
import Entity from '@/common-utils/components/renders/entity/Entity';
import Panel from '@/common-utils/components/renders/panel/Panel';
import RenderApplication from '../application/RenderApplication';
import RenderBankCode from '../bankCode/RenderBankCode';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/common-utils/components/ui/form';
import { Input } from '@/common-utils/components/ui/input';
// Style
import './RanksUpdate.scss';
// Lang
import { nsAccount } from '@/app/i18n/namespaces';

/**
 * @param root0
 * @param root0.contract
 * @param root0.contractsOnError
 * @param root0.setContractsOnError
 * @returns {JSX} RanksUpdate
 */
function RanksUpdate({ contract, contractsOnError, setContractsOnError }) {

    // Translate
    const { t: translate } = useTranslation();

    // State Init
    const getDefaultOption = () => {
        let defaultOption = 'select-custom-rank';

        if (!contract.newRank) {
            defaultOption = 'select-all-ranks';
        } else if (contract.newRank.includes('_')) {
            defaultOption = 'select-rank-range';
        }

        return defaultOption;
    };

    // State
    const [checkedOption, setCheckedOption] = useState(getDefaultOption());
    const [from, setFrom] = useState();
    const [to, setTo] = useState();

    // Form definition
    const formSchema = z.object({
        label: z.string().max(100),
        ranks: z.string().regex(/^(\s*\d{3}\s*)(,\s*\d{3}\s*)*$/, { message: translate('form.ranks.free_input') }),
        fromRank: z.string().regex(/^\d{3}$/, { message: translate('form.ranks.digits_input') }),
        toRank: z.string().regex(/^\d{3}$/, { message: translate('form.ranks.digits_input') })
    });

    const form = useForm({
        resolver: zodResolver(formSchema),
        mode: 'onChange',
        defaultValues: {
            label: contract.merchantLabel ?? '',
            ranks: contract.newRank ?? '',
            fromRank: contract.newRank ? contract.newRank.split('_')[0] : '',
            toRank: contract.newRank ? contract.newRank.split('_')[1] : ''
        }
    });

    useEffect(() => {
        if (Object.keys(form.formState.errors).length !== 0
            || (checkedOption !== 'select-all-ranks' && (contract.newRank === '' || !contract.newRank))
            || (checkedOption === 'select-rank-range' && (!contract.newRank || contract.newRank.length !== 7))) {
            const array = [...contractsOnError];
            array.push(contract.uuid);
            setContractsOnError(array);
        } else {
            const array = contractsOnError.filter(elem => elem !== contract.uuid);
            setContractsOnError(array);
        }
    }, [Object.keys(form.formState.errors).length, checkedOption, contract.newRank]);

    return (
        <Panel>
            <div className='flex p-2'>
                <div className='flex  flex-col flex-1 text-center justify-evenly gap-2'>
                    <Entity
                        entity={'modal.create_merchant_account.texts.contract'}
                        namespace={nsAccount}
                        data={{ contractNumber: contract.number }}
                    />
                    <RenderBankCode
                        bankCode={contract.bankCode}
                        label={'modal.create_merchant_account.texts.bank_code'}
                        namespace={nsAccount}
                    />
                    <RenderApplication applicationCode={contract.application} />
                </div>
                <div className='flex-3'>
                    <Form {...form}>
                        <form className='space-y-4'>
                            <div className='flex flex-col m-2'>
                                <div className='flex  flex-1 mx-2'>
                                    <FormField
                                        control={form.control}
                                        name='label'
                                        className='flex-1'
                                        render={({ field }) => (
                                            <FormItem className='flex-1'>
                                                <FormLabel>
                                                    <Entity namespace={nsAccount} entity='modal.create_merchant_account.labels.label' />
                                                </FormLabel>
                                                <FormControl>
                                                    <Input
                                                        namespace={nsAccount}
                                                        entity='modal.create_merchant_account.placeholders.label'
                                                        {...field}
                                                        onInput={(e) => {
                                                            contract.merchantLabel = e.target.value;
                                                        }}
                                                    />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                </div>
                                <div className='flex-1 m-2'>
                                    <div className='radio-options flex' >
                                        <div className='options-label flex-1'>
                                            <label>
                                                <input
                                                    className='mr-2'
                                                    type='radio'
                                                    name='test'
                                                    value={'select-all-ranks'}
                                                    checked={checkedOption === 'select-all-ranks'}
                                                    onChange={(event) => {
                                                        setCheckedOption(event.target.value);
                                                        contract.newRank = null;
                                                        form.setValue('fromRank', '');
                                                        form.setValue('toRank', '');
                                                        form.setValue('ranks', '');
                                                        form.clearErrors();
                                                    }}
                                                />
                                                <Entity entity='modal.create_merchant_account.labels.ranks.all' namespace={nsAccount} className={'text-sm'} />
                                            </label>
                                        </div>
                                        <div className='options-label flex-1'>
                                            <label>
                                                <input
                                                    className='mr-2'
                                                    name='test'
                                                    type='radio' value={'select-custom-rank'}
                                                    checked={checkedOption === 'select-custom-rank'}
                                                    onChange={(event) => {
                                                        setCheckedOption(event.target.value);
                                                        contract.newRank = null;
                                                        form.setValue('ranks', '');
                                                        form.setValue('fromRank', '');
                                                        form.setValue('toRank', '');
                                                        form.clearErrors();
                                                    }}
                                                />
                                                <Entity entity='modal.create_merchant_account.labels.ranks.simple' namespace={nsAccount} className={'text-sm'} />
                                            </label>
                                        </div>
                                        <div className='options-label flex-1'>
                                            <label>
                                                <input
                                                    className='mr-2'
                                                    name='test'
                                                    type='radio' value={'select-rank-range'}
                                                    checked={checkedOption === 'select-rank-range'}
                                                    onChange={(event) => {
                                                        setCheckedOption(event.target.value);
                                                        contract.newRank = null;
                                                        form.setValue('ranks', '');
                                                        form.setValue('fromRank', '');
                                                        form.setValue('toRank', '');
                                                        form.clearErrors();
                                                    }}
                                                />
                                                <Entity entity='modal.create_merchant_account.labels.ranks.range' namespace={nsAccount} className={'text-sm'} />
                                            </label>
                                        </div>
                                    </div>
                                    {
                                        checkedOption === 'select-custom-rank' &&

                                        <FormField
                                            control={form.control}
                                            name='ranks'
                                            render={({ field }) => (
                                                <FormItem className='mt-2'>
                                                    <FormControl>
                                                        <Input
                                                            namespace={nsAccount}
                                                            entity='modal.create_merchant_account.placeholders.ranks.simple'
                                                            {...field}
                                                            onInput={(e) => {
                                                                contract.newRank = e.target.value;
                                                            }}
                                                        />
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />
                                    }
                                    {
                                        checkedOption === 'select-rank-range' &&
                                        <div className='flex mt-2'>
                                            <FormField
                                                control={form.control}
                                                name='fromRank'
                                                render={({ field }) => (
                                                    <FormItem className='flex flex-1 flex-col'>
                                                        <div className='flex flex-1 items-baseline'>
                                                            <FormLabel>
                                                                <Entity namespace={nsAccount} entity='modal.create_merchant_account.labels.ranks.from' />
                                                            </FormLabel>
                                                            <FormControl className='mx-2'>
                                                                <Input
                                                                    namespace={nsAccount}
                                                                    entity='modal.create_merchant_account.placeholders.ranks.range'
                                                                    {...field}
                                                                    onInput={(e) => {
                                                                        setFrom(e.target.value);
                                                                        contract.newRank = `${e.target.value}_${to}`;
                                                                    }}
                                                                />
                                                            </FormControl>
                                                        </div>
                                                        <FormMessage />


                                                    </FormItem>
                                                )}
                                            />
                                            <FormField
                                                control={form.control}
                                                name='toRank'
                                                render={({ field }) => (
                                                    <FormItem className='flex flex-col flex-1'>
                                                        <div className='flex flex-1 items-baseline'>
                                                            <FormLabel className='mr-2'>
                                                                <Entity namespace={nsAccount} entity='modal.create_merchant_account.labels.ranks.to' />
                                                            </FormLabel>
                                                            <FormControl>
                                                                <Input
                                                                    namespace={nsAccount}
                                                                    entity='modal.create_merchant_account.placeholders.ranks.range'
                                                                    {...field}
                                                                    onInput={(e) => {
                                                                        setTo(e.target.value);
                                                                        contract.newRank = `${from}_${e.target.value}`;
                                                                    }}
                                                                />
                                                            </FormControl>
                                                        </div>
                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />
                                        </div>
                                    }
                                </div>
                            </div>
                        </form>
                    </Form>
                </div>
            </div>
        </Panel>
    );
}

RanksUpdate.propTypes = {
    contract: PropTypes.object.isRequired,
    contractsOnError: PropTypes.array.isRequired,
    setContractsOnError: PropTypes.func.isRequired
};

export default RanksUpdate;